import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Dashboard da Associação (at-86) — leitura agregada da carteira, para CONTAC e ADMIN.
 *
 * ⚠️ Nenhuma função aqui engole erro (at-60). Quem chama precisa distinguir "o período não teve
 * movimento" de "não consegui perguntar": as duas coisas produzem gráfico vazio, e confundi-las
 * faz a diretoria ler um mês zerado como fato do negócio.
 */

/** Filtros comuns a todas as abas. Vazio = período padrão e todas as bases. */
export interface FiltrosDashboard {
  dataInicio?: string;
  dataFim?: string;
  baseTag?: string;
  codigoRegional?: string;
  categoria?: string;
}

export interface JanelaConsultada {
  dataInicio: string;
  dataFim: string;
  /**
   * Última data com dado disponível no Contac. A tela usa isto para explicar por que o mês
   * corrente não aparece — sem a explicação, o buraco no fim do gráfico parece perda de dado.
   */
  maximoDisponivel: string;
}

export interface FrotaKpis {
  veiculosAtivos: number;
  veiculosInativos: number;
  veiculosSituacaoDesconhecida: number;
  veiculosTotal: number;
  valorProtegidoAtivo: number;
  valorProtegidoMedio: number;
  associadosComVeiculoAtivo: number;
}

export interface ItemCategoria {
  categoria: string;
  quantidade: number;
  quantidadeAtiva: number;
  valorProtegido: number;
}

export interface ItemFaixa {
  faixa: string;
  quantidade: number;
}

export interface ItemTipo {
  tipo: string;
  quantidade: number;
}

export interface ItemCrescimento {
  competencia: string;
  adesoes: number;
  saidas: number;
  liquido: number;
}

export interface ItemCarteira {
  codigoVoluntario: string;
  consultor: string;
  veiculosAtivos: number;
  valorProtegido: number;
}

export interface RespostaFrota {
  janela: JanelaConsultada;
  kpis: FrotaKpis;
  porCategoria: ItemCategoria[];
  porTipo: ItemTipo[];
  idadeFrota: ItemFaixa[];
  faixaEtariaAssociados: ItemFaixa[];
  crescimentoMensal: ItemCrescimento[];
  carteiraPorConsultor: ItemCarteira[];
}

export interface VisaoGeralKpis {
  valorEmitido: number;
  valorArrecadado: number;
  boletosEmitidos: number;
  boletosPagos: number;
  /** Já em pontos percentuais (75 = 75%), calculado no backend para a tela não divergir dele. */
  taxaAdimplencia: number;
  ticketMedio: number;
}

export interface ItemArrecadacao {
  competencia: string;
  emitido: number;
  arrecadado: number;
  boletos: number;
  boletosPagos: number;
}

export interface ItemSituacaoBoleto {
  situacao: string;
  quantidade: number;
  valor: number;
}

export interface ItemBase {
  base: string;
  arrecadado: number;
  boletos: number;
}

export interface RespostaVisaoGeral {
  janela: JanelaConsultada;
  kpis: VisaoGeralKpis;
  arrecadacaoMensal: ItemArrecadacao[];
  porSituacaoBoleto: ItemSituacaoBoleto[];
  porBase: ItemBase[];
  /**
   * Filtros enviados que NÃO valem nesta aba (o valor é por boleto; regional e categoria são do
   * veículo). A tela precisa exibir isso: um filtro aplicado que não muda o número é lido como
   * resposta — "a regional X arrecada o mesmo que a empresa toda".
   */
  filtrosNaoAplicados: string[];
}

const paramsDe = (filtros: FiltrosDashboard) => ({
  ...(filtros.dataInicio ? { dataInicio: filtros.dataInicio } : {}),
  ...(filtros.dataFim ? { dataFim: filtros.dataFim } : {}),
  ...(filtros.baseTag ? { base_tag: filtros.baseTag } : {}),
  ...(filtros.codigoRegional ? { codigoRegional: filtros.codigoRegional } : {}),
  ...(filtros.categoria ? { categoria: filtros.categoria } : {}),
});

export const buscarVisaoGeral = async (
  filtros: FiltrosDashboard,
): Promise<RespostaVisaoGeral> => {
  const { data } = await axios.get<RespostaVisaoGeral>(`${BACKEND_URL}/contac-dashboard/visao-geral`, {
    params: paramsDe(filtros),
    headers: authHeader(),
  });

  return data;
};

export const buscarFrota = async (filtros: FiltrosDashboard): Promise<RespostaFrota> => {
  const { data } = await axios.get<RespostaFrota>(`${BACKEND_URL}/contac-dashboard/frota`, {
    params: paramsDe(filtros),
    headers: authHeader(),
  });

  return data;
};
